import { FactionTask } from "@1games-ru/region-shared/dist/types/faction/faction";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import { ArrowDownIcon, Document3Icon, Info2Icon, Star3Icon } from "@/shared/icons";
import { Button, Select, Tooltip } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import FactionOptionItem from "@/domains/faction/components/FactionOptionItem";
import FactionProgressLine from "@/domains/faction/components/FactionProgressLine";
import FactionTooltipContent from "@/domains/faction/components/FactionTooltipContent";

type Props = {
  tasksList: FactionTask[];
  src: string;
  currentCount: number;
  nextLevelCount: number;
  onClick: () => void;
  count?: number;
  isControl?: boolean;
  factionName: FactionEnum;
  className?: string;
};

export default function FactionExperience({
  tasksList,
  src,
  currentCount,
  nextLevelCount,
  onClick,
  count,
  isControl,
  factionName,
  className,
}: Props) {
  const { t } = useTranslation("faction");
  const nextLevelProgress =
    typeof currentCount === "number" && typeof nextLevelCount === "number"
      ? (currentCount * 100) / nextLevelCount
      : 0;

  let lineColor = "bg-[#2B3830]";
  let textColor = "text-[#CDE5D6]";
  let btnColor = "bg-[#75AA8A]";
  let btnColorHover = "hover:bg-[#629276]";
  let selectColor = "bg-[#2B3830]";

  if (factionName === FactionEnum.HOSPITAL) {
    lineColor = "bg-[#382B2B]";
    textColor = "text-[#E5CDCD]";
    btnColor = "bg-[#A53939]";
    btnColorHover = "hover:bg-[#943131]";
    selectColor = "bg-[#382B2B]";
  }
  if (factionName === FactionEnum.MEDSI || factionName === FactionEnum.REGION24) {
    lineColor = "bg-[#022840]";
    textColor = "text-[#CDD8E5]";
    btnColor = "bg-[#1A5EAC]";
    btnColorHover = "hover:bg-[#1757A1]";
    selectColor = "bg-[#022840]";
  }

  const tooltipContent = (
    <FactionTooltipContent
      title={t("faction.tooltip.2.title")}
      description={t("faction.tooltip.2.description")}
      className="rounded-br-none"
    />
  );

  return (
    <div
      className={cn("p-4 bg-cover bg-no-repeat bg-center rounded-2xl", className)}
      style={{ backgroundImage: `url(${src})` }}
    >
      <div className="flex items-center mb-3">
        <Star3Icon className="size-4.5" />
        <p className="ml-2 text-14 font-bold uppercase">{t("faction.progressOfRank")}</p>
        <Select
          className="z-[4] w-[13.5rem] min-h-7 h-7 ml-auto rounded-md"
          classNameSelect={cn("px-2 !rounded-[0.375rem]", selectColor)}
          classNamePlaceholder="text-[0.75rem] font-bold text-white uppercase "
          placeholder={t("faction.tasksForUp")}
          Icon={ArrowDownIcon}
          classNameIcon="size-2.5"
          optionsStyle={{ boxShadow: "none" }}
          classNameOptopns={clsx(
            "top-8.5 p-[0.9375rem] border border-[#242424] rounded-2xl",
            !tasksList || (tasksList?.length === 0 && "hidden"),
          )}
        >
          {tasksList?.length > 0 && (
            <div className="flex flex-col gap-[0.8125rem]">
              {tasksList.map((item) => (
                <FactionOptionItem
                  key={item.id}
                  text={item.name}
                  currentCount={item.currentProgress}
                  maxCount={item.needProgress}
                  isCompleted={item.isCompleted}
                />
              ))}
            </div>
          )}
        </Select>
        <Button
          variant="solid"
          className={cn(
            "h-7 ml-1.5 py-1.5 px-2 leading-none rounded-md transition-all duration-200",
            btnColor,
            btnColorHover,
          )}
          onClick={onClick}
        >
          {t("faction.upRank")}
        </Button>
      </div>
      <FactionProgressLine
        progress={nextLevelProgress}
        className="h-3 mb-2.5 p-0 border-none"
        classNameInnerLine={cn("p-[0.1875rem]", lineColor)}
        classNameActiveLine="h-1.5"
      />
      <div className="flex items-center">
        <p className={cn("text-14 font-medium", textColor)}>
          {isControl ? t("faction.tasksForNextRank") : t("faction.participantsPoints")}:
        </p>
        <div className="flex items-center gap-0.5 ml-auto pt-0.5">
          {isControl ? (
            <>
              <p className="text-14 font-bold leading-none">{currentCount}</p>
              <Star3Icon className="size-3.5 ml-1" />
              <div className={cn("w-px h-2.5 mx-2", lineColor)} />
              <p className="text-14 font-bold leading-none">{count} ОУ</p>
              <Tooltip
                content={tooltipContent}
                className="z-1 ml-auto"
                tooltipClassName="bottom-7 right-4.5"
              >
                <Info2Icon className="size-4.5 ml-1.5 text-white/30 transition-all duration-200 hover:text-white" />
              </Tooltip>
            </>
          ) : (
            <>
              <p className="text-14 font-bold">{currentCount}</p> /{" "}
              <p className={cn("text-[0.875rem] font-bold", textColor)}>{nextLevelCount}</p>
            </>
          )}
        </div>
        {!isControl && <Document3Icon className="size-3.5 ml-1" />}
      </div>
    </div>
  );
}
